import { useEffect, useRef, useState } from 'react';
import type { Game } from '../app/game';

const OBJECTIVES = [
  { title: 'Build and gather', body: 'Select your Core, queue a worker, then adjust the Power / Essence split. H recalls or resumes the selected zone.' },
  { title: 'Capture and ascend', body: 'Play a unit, select it, press C, and click a neutral strategic site. Hold the site to reach Ascension II.' },
  { title: 'Build and produce', body: 'Place a production building, select it, queue a roster unit, and try its auto-production toggle or upgrade.' },
  { title: 'Boss and escort', body: 'Play an Ascension II boss. Select another squad, press E, and click the boss to form an escort.' },
  { title: 'Destroy the Core', body: 'Advance through the lane, suppress the enemy Core defenses, and destroy the Core to finish training.' },
] as const;

export function TutorialOverlay({ game, onExit }: { game: Game; onExit: () => void }) {
  const [stage, setStage] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const suppliedAt = useRef(-1);
  const st = game.sim.state;
  const player = st.players[game.localPlayer];

  const complete = (() => {
    switch (stage) {
      case 0:
        return [...st.workers.values()].some((worker) => worker.owner === game.localPlayer);
      case 1:
        return player.ascensionDiscovered >= 2 && st.sites.some((site) => site.owner === game.localPlayer);
      case 2:
        return player.stats.unitsBuilt > 0 && [...st.buildings.values()].some((building) => building.owner === game.localPlayer && !building.isCore && building.state === 'active');
      case 3: {
        const boss = [...st.squads.values()].some((squad) => squad.owner === game.localPlayer && squad.isBoss);
        const escort = [...st.squads.values()].some((squad) => squad.owner === game.localPlayer && !squad.isBoss && (squad.command.kind === 'escort' || squad.mountedOnSquad !== null));
        return boss && escort;
      }
      case 4:
        return st.phase === 'ended' && st.winningTeam === st.players[game.localPlayer].team;
      default:
        return false;
    }
  })();

  useEffect(() => {
    if (!complete) return;
    if (stage === OBJECTIVES.length - 1) {
      try { window.localStorage.setItem('pa:tutorial:v1', 'complete'); } catch { /* best effort */ }
      return;
    }
    const id = window.setTimeout(() => setStage((value) => Math.min(OBJECTIVES.length - 1, value + 1)), 900);
    return () => window.clearTimeout(id);
  }, [complete, stage]);

  useEffect(() => {
    if (stage === 0 || suppliedAt.current === stage) return;
    suppliedAt.current = stage;
    game.cheat(650, 300);
  }, [game, stage]);

  const objective = OBJECTIVES[stage];
  return (
    <aside className={`tutorialpanel${collapsed ? ' collapsed' : ''}`}>
      <div className="tutorialhead">
        <span>TUTORIAL · {stage + 1}/{OBJECTIVES.length}</span>
        <button onClick={() => setCollapsed((value) => !value)}>{collapsed ? '▸' : '▾'}</button>
      </div>
      {!collapsed && (
        <>
          <div className="tutorialprogress"><i style={{ width: `${((stage + (complete ? 1 : 0)) / OBJECTIVES.length) * 100}%` }} /></div>
          <h3>{complete ? 'Objective complete' : objective.title}</h3>
          <p>{complete ? 'Preparing the next lesson…' : objective.body}</p>
          <div className="tutorialactions">
            <button onClick={() => game.cheat(500, 250)}>Grant supplies</button>
            {stage < OBJECTIVES.length - 1 && <button onClick={() => setStage((value) => value + 1)}>Skip objective</button>}
            <button onClick={onExit}>Exit training</button>
          </div>
        </>
      )}
    </aside>
  );
}
